import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import { getActiveRole } from '../utils/sessionKeys';
import useNotificationSocket from '../hooks/useNotificationSocket';

const ChatContext = createContext();

// Normalize message fields from Socket.IO (camelCase) to match REST API format (snake_case)
// that the UI components expect (sender_id, content, sent_at, etc.)
const normalizeMessage = (msg) => ({
  id: msg.id || msg.ID,
  consultation_id: msg.consultation_id || msg.consultationId,
  sender_id: msg.sender_id || msg.senderId,
  sender_type: msg.sender_type || msg.senderType,
  recipient_id: msg.recipient_id || msg.recipientId,
  recipient_type: msg.recipient_type || msg.recipientType,
  content: msg.content || msg.message,
  sent_at: msg.sent_at || msg.SentAt || msg.timestamp,
  message_type: msg.message_type || msg.messageType,
  delivery_status: msg.delivery_status || msg.deliveryStatus,
  is_queued: msg.is_queued || false,
  queue_status: msg.queue_status || null,
});

export const ChatProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  useNotificationSocket(socket);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [error, setError] = useState(null);

  // Configure Socket.IO with the backend endpoint
  useEffect(() => {
    // Use the same origin as the page so Socket.IO goes through nginx proxy on port 3000.
    // REACT_APP_API_URL is the Docker-internal URL (http://backend:8080) which browsers
    // cannot resolve. By using window.location.origin, nginx proxies /socket.io/ to backend.
    const SOCKET_ENDPOINT = window.location.origin;
    console.log('Initializing Socket.IO connection to:', SOCKET_ENDPOINT);

    const socketInstance = io(SOCKET_ENDPOINT, {
      transports: ['websocket'], // WebSocket-only avoids polling→websocket upgrade race condition
      autoConnect: false,
      withCredentials: true,
    });

    // Socket transport events (NOT the same as "ready to chat")
    socketInstance.on('connect', () => {
      console.log('Socket.IO transport connected with ID:', socketInstance.id);
      setError(null);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('Socket.IO disconnected:', reason);
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (err) => {
      console.error('Socket.IO connection error:', err);
      setError(`Connection error: ${err.message}`);
      setIsConnected(false);
    });

    socketInstance.on('error', (err) => {
      console.error('Socket.IO error:', err);
      setError(`Socket error: ${err.message || err}`);
    });

    // Auth events
    socketInstance.on('auth-error', (data) => {
      console.error('Socket.IO auth error:', data);
      setError(`Authentication error: ${data.error}`);
    });

    // Joined consultation room - now fully ready to send/receive
    socketInstance.on('joined-consultation', (data) => {
      console.log('Joined consultation room:', data);
      setIsConnected(true);
    });

    socketInstance.on('join-error', (data) => {
      console.error('Socket.IO join error:', data);
      setError(`Join error: ${data.error}`);
    });

    // Message history sent by backend on room join (before joined-consultation)
    socketInstance.on('message-history', (data) => {
      console.log(`Received ${data.totalMessages} messages from history`);
      const historyMessages = (data.messages || []).map(normalizeMessage);
      setMessages(historyMessages);
    });

    // Real-time incoming messages
    socketInstance.on('new-message', (messageData) => {
      console.log('New message received:', messageData);
      const normalized = normalizeMessage(messageData);
      setMessages(prev => {
        // Deduplicate by id
        if (normalized.id && prev.some(m => m.id === normalized.id)) {
          return prev;
        }
        return [...prev, normalized];
      });
    });

    // Typing indicators - backend broadcasts 'typing' and 'stop-typing' events
    socketInstance.on('typing', (data) => {
      setTypingUsers(prev => ({
        ...prev,
        [data.userId]: {
          id: data.userId,
          type: data.userType,
          isTyping: true,
        }
      }));
    });

    socketInstance.on('stop-typing', (data) => {
      setTypingUsers(prev => {
        const newState = { ...prev };
        delete newState[data.userId];
        return newState;
      });
    });

    // System updates
    socketInstance.on('consultation-update', (data) => {
      console.log('Consultation update:', data);
      if (data.type === 'system') {
        setMessages(prev => [...prev, normalizeMessage({
          content: data.message,
          senderType: 'system',
          timestamp: data.timestamp,
        })]);
      }
    });

    // Message queue events
    socketInstance.on('message_sent', (data) => {
      console.log('Message sent confirmation:', data);
      setMessages(prev =>
        prev.map(msg =>
          (msg.client_message_id && msg.client_message_id === data.client_message_id) ||
          msg.id === (data.messageId || data.message_id || data.id)
            ? {
                ...normalizeMessage(data.message || data),
                is_queued: data.is_queued || false,
                queue_status: data.is_queued ? 'pending' : null
              }
            : msg
        )
      );
    });

    socketInstance.on('queued_message_delivered', (data) => {
      console.log('Queued message delivered:', data);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === data.message_id
            ? {
                ...msg,
                delivery_status: 'delivered',
                delivered_at: data.delivered_at,
                is_queued: false,
                queue_status: 'delivered'
              }
            : msg
        )
      );
    });

    socketInstance.on('queued_message_failed', (data) => {
      console.log('Queued message failed:', data);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === data.message_id
            ? {
                ...msg,
                queue_status: 'failed',
                last_error: data.error
              }
            : msg
        )
      );
    });

    socketInstance.on('pending_messages', (data) => {
      console.log(`Receiving ${data.count} pending messages`);
      const newMessages = (data.messages || []).map(msg => ({
        ...normalizeMessage(msg),
        is_queued: true,
        queue_status: 'delivered'
      }));
      setMessages(prev => [...prev, ...newMessages]);
    });

    socketInstance.on('consultation_ready', (data) => {
      console.log('Consultation ready:', data);
      setMessages(prev => [...prev, normalizeMessage({
        content: `Your consultation with ${data.astrologer?.first_name || 'astrologer'} is ready!`,
        senderType: 'system',
        timestamp: data.auto_created_at || new Date().toISOString()
      })]);
    });

    socketInstance.on('session_ending', (data) => {
      console.log('Session ending warning:', data);
      setMessages(prev => [...prev, normalizeMessage({
        content: `Session will end in ${data.time_remaining} seconds`,
        senderType: 'system',
        timestamp: new Date().toISOString()
      })]);
    });

    socketInstance.on('session_ended', (data) => {
      console.log('Session ended:', data);
      setMessages(prev => [...prev, normalizeMessage({
        content: 'Consultation session completed',
        senderType: 'system',
        timestamp: new Date().toISOString()
      })]);
    });

    // Expose socket globally for on-demand features (status toggle, listing page)
    window.__astroSocket = socketInstance;

    setSocket(socketInstance);

    return () => {
      console.log('Cleaning up Socket.IO connection');
      window.__astroSocket = null;
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };
  }, []);

  // Connect globally (no room join) — for listing pages that need status updates
  const connectGlobal = useCallback((userId, userType) => {
    if (!socket) return;
    const role = getActiveRole();
    // Token is in httpOnly cookie — Socket.IO sends it via the HTTP upgrade handshake.
    // We pass an empty token; the backend extracts it from the cookie.
    const token = null;
    const doAuth = () => {
      socket.emit('authenticate', { token, userId: Number(userId), userType });
    };
    if (!socket.connected) {
      socket.once('connect', doAuth);
      socket.connect();
    } else {
      doAuth();
    }
  }, [socket]);

  // Connect to consultation with proper auth flow:
  // 1. Connect transport  2. Authenticate  3. Join consultation room
  const connect = (consultationId, userId, astrologerId) => {
    if (!socket) {
      setError('Socket not initialized');
      return;
    }

    console.log('Connecting to consultation:', consultationId);

    const isUser = !!userId;
    const authId = isUser ? userId : astrologerId;
    const userType = isUser ? 'user' : 'astrologer';

    if (!authId) {
      setError('Either userId or astrologerId must be provided');
      return;
    }

    // Clear state for new consultation
    setMessages([]);
    setTypingUsers({});
    setIsConnected(false);

    // Remove stale one-time handlers to avoid stacking on re-renders
    socket.off('auth-success');

    // Step 3: After auth succeeds, join the consultation room
    socket.once('auth-success', (data) => {
      console.log('Authenticated successfully:', data);
      socket.emit('join-consultation', { consultationId: Number(consultationId) });
    });

    // Step 2: Authenticate with JWT token (with legacy userId/userType fallback)
    const role = getActiveRole();
    // Token is in httpOnly cookie — Socket.IO sends it via the HTTP upgrade handshake.
    // We pass an empty token; the backend extracts it from the cookie.
    const token = null;
    const doAuth = () => {
      console.log('Authenticating as', userType, authId);
      socket.emit('authenticate', {
        token,
        userId: Number(authId),
        userType: userType,
      });
    };

    // Step 1: Connect transport if needed
    if (!socket.connected) {
      socket.once('connect', doAuth);
      socket.connect();
    } else {
      doAuth();
    }
  };

  // Disconnect from consultation
  const disconnect = (consultationId) => {
    if (socket && socket.connected) {
      console.log('Disconnecting from consultation:', consultationId);

      if (consultationId) {
        socket.emit('leave-consultation', { consultationId: Number(consultationId) });
      }

      socket.disconnect();

      setIsConnected(false);
      setMessages([]);
      setTypingUsers({});
    }
  };

  // Send a message - accepts object with consultation_id/consultationId and content/message
  // Returns clientMessageId for tracking, or null on failure
  const sendMessage = (messageData) => {
    if (socket && isConnected) {
      const clientMessageId = crypto.randomUUID();
      const consultationId = Number(messageData.consultationId || messageData.consultation_id);
      const content = messageData.message || messageData.content;

      console.log('Sending message:', { consultationId, clientMessageId });

      // Emit with client_message_id for dedup (backend silently ignores for now)
      socket.emit('send-message', {
        consultationId,
        message: content,
        client_message_id: clientMessageId,
      });

      // Optimistic render — add message to state immediately
      setMessages(prev => [...prev, {
        id: clientMessageId,
        client_message_id: clientMessageId,
        consultation_id: consultationId,
        content: content,
        sender_id: messageData.sender_id || null,
        sender_type: messageData.sender_type || 'user',
        sent_at: new Date().toISOString(),
        message_type: 'text',
        delivery_status: 'sent',
        is_queued: false,
        queue_status: null,
      }]);

      return clientMessageId;
    } else {
      console.error('Cannot send message: Socket not connected');
      setError('Socket not connected');
      return null;
    }
  };

  // Send typing indicator - uses separate events for start/stop
  const sendTypingIndicator = (consultationId, userId, userType, isTyping) => {
    if (socket && isConnected) {
      const event = isTyping ? 'typing' : 'stop-typing';
      socket.emit(event, {
        consultationId: Number(consultationId),
      });
    }
  };

  const value = {
    socket,
    isConnected,
    messages,
    typingUsers,
    error,
    connect,
    connectGlobal,
    disconnect,
    sendMessage,
    sendTypingIndicator,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
